import type { QueueIconColor, QueueIconMeaning } from '@tenfour/contracts'
import type { ChromeIconName } from '@/components/ChromeIcon'

export type QuickActionGroup = 'Quick action' | 'Navigate' | 'Account'

/**
 * The 44px mark on an action row and the 64px identity mark in its preview.
 * Clay for destinations, chrome for shell meanings — never a Lucide glyph
 * where an entity or chrome mark exists (design system: "Clay for
 * destinations. Lucide for controls.").
 */
export type QuickActionMark =
  | { kind: 'entity'; meaning: QueueIconMeaning; color: QueueIconColor }
  | { kind: 'chrome'; name: ChromeIconName }
  | { kind: 'page'; pathname: string }

/**
 * One row in the command dialog's Quick actions group. `to` navigates; `run`
 * fires an imperative handler the dialog owns (there is no action with both).
 */
export interface QuickAction {
  key: string
  label: string
  mark: QuickActionMark
  group: QuickActionGroup
  /**
   * What the row does — its meta line and the preview's "Does" fact. Navigate
   * rows leave this unset and borrow the page catalog's description
   * (`PAGES` in useGlobalSearch), so the two never drift.
   */
  description?: string
  /** Extra terms the dialog's filter matches, beyond `label`. */
  keywords?: string
  /** Verb on the preview footer's button and in the identity line. */
  verb: string
  /** Hidden for a member without the View reports permission. */
  requiresReportsPerm?: boolean
  to?: string
  run?: 'search' | 'help' | 'signOut'
  /** The global key that does the same thing, when one exists (shortcuts.ts);
   *  the preview names it. Most actions have none. */
  shortcut?: string
}

/**
 * The registry. The `Quick action` group is the retired sidebar Create
 * launcher verbatim — each entry deep-links with `?create=<entity>`, which the
 * destination page's effect turns into an open create form. Quote is a kind
 * of invoice, so it shares the invoice route.
 */
export const QUICK_ACTIONS: QuickAction[] = [
  {
    key: 'create-customer',
    label: 'New customer',
    mark: { kind: 'entity', meaning: 'customer', color: 'blue' },
    group: 'Quick action',
    description: 'Opens the new customer form',
    keywords: 'add create client contact person prospect',
    verb: 'Create',
    to: '/customers-v2?create=customer',
  },
  {
    key: 'create-quote',
    label: 'New quote',
    mark: { kind: 'entity', meaning: 'invoice', color: 'yellow' },
    group: 'Quick action',
    description: 'Starts a quote in the invoice workspace',
    keywords: 'add create estimate bid proposal',
    verb: 'Create',
    to: '/invoices?create=invoice',
  },
  {
    key: 'create-invoice',
    label: 'New invoice',
    mark: { kind: 'entity', meaning: 'invoice', color: 'green' },
    group: 'Quick action',
    description: 'Starts an invoice in the invoice workspace',
    keywords: 'add create bill charge money',
    verb: 'Create',
    to: '/invoices?create=invoice',
  },
  {
    key: 'create-job',
    label: 'New job',
    mark: { kind: 'entity', meaning: 'job', color: 'green' },
    group: 'Quick action',
    description: 'Opens the new job form',
    keywords: 'add create work order request appointment',
    verb: 'Create',
    to: '/jobs?create=job',
  },
  {
    key: 'create-meeting',
    label: 'New meeting',
    mark: { kind: 'entity', meaning: 'meeting', color: 'aqua' },
    group: 'Quick action',
    description: 'Opens the schedule meeting form',
    keywords: 'add create google meet video call schedule huddle',
    verb: 'Create',
    to: '/calendar/meetings?create=meeting',
  },
  {
    key: 'create-reminder',
    label: 'New reminder',
    mark: { kind: 'entity', meaning: 'reminder', color: 'lavender' },
    group: 'Quick action',
    description: 'Opens the new reminder form',
    keywords: 'add create task todo follow up',
    verb: 'Create',
    to: '/reminders?create=reminder',
  },
  {
    key: 'search-records',
    label: 'Search records',
    mark: { kind: 'chrome', name: 'search' },
    group: 'Quick action',
    description: 'Finds customers, jobs, invoices, and pages',
    keywords: 'find lookup customers jobs invoices pages everything',
    verb: 'Search records',
    run: 'search',
    shortcut: '/',
  },

  { key: 'go-home', label: 'Home', mark: { kind: 'page', pathname: '/home' }, group: 'Navigate', keywords: 'dashboard for you overview', verb: 'Open', to: '/home' },
  { key: 'go-attention', label: 'Needs your attention', mark: { kind: 'page', pathname: '/queue' }, group: 'Navigate', keywords: 'queue attention needs you decisions replies calls waiting todo triage', verb: 'Open', to: '/queue' },
  { key: 'go-calls', label: 'Calls', mark: { kind: 'page', pathname: '/calls' }, group: 'Navigate', keywords: 'phone call transcript history', verb: 'Open', to: '/calls' },
  { key: 'go-messages', label: 'Messages', mark: { kind: 'page', pathname: '/messages' }, group: 'Navigate', keywords: 'conversations sms texts threads inbox', verb: 'Open', to: '/messages' },
  { key: 'go-customers', label: 'Customers', mark: { kind: 'page', pathname: '/customers-v2' }, group: 'Navigate', keywords: 'clients contacts crm list', verb: 'Open', to: '/customers-v2' },
  { key: 'go-money', label: 'Money', mark: { kind: 'page', pathname: '/money' }, group: 'Navigate', keywords: 'invoices payments payouts revenue billing', verb: 'Open', to: '/money' },
  { key: 'go-jobs', label: 'Jobs', mark: { kind: 'page', pathname: '/jobs' }, group: 'Navigate', keywords: 'queue jobs work orders schedule', verb: 'Open', to: '/jobs' },
  { key: 'go-forms', label: 'Forms', mark: { kind: 'page', pathname: '/forms' }, group: 'Navigate', keywords: 'forms booking form builder publish responses questions', verb: 'Open', to: '/forms' },
  { key: 'go-meetings', label: 'Meetings', mark: { kind: 'page', pathname: '/calendar' }, group: 'Navigate', keywords: 'meetings meet video call transcript summary schedule', verb: 'Open', to: '/calendar/meetings' },
  { key: 'go-reminders', label: 'Reminders', mark: { kind: 'page', pathname: '/reminders' }, group: 'Navigate', keywords: 'tasks todos due dates', verb: 'Open', to: '/reminders' },
  { key: 'go-reports', label: 'Reports', mark: { kind: 'page', pathname: '/reports' }, group: 'Navigate', keywords: 'analytics earnings charts export csv', verb: 'Open', to: '/reports', requiresReportsPerm: true },

  { key: 'go-settings', label: 'Account settings', mark: { kind: 'chrome', name: 'settings' }, group: 'Account', description: 'Business info, branding, services, payments, and team', keywords: 'business info branding services payments team security preferences', verb: 'Open', to: '/settings' },
  { key: 'help', label: 'Help & support', mark: { kind: 'chrome', name: 'help' }, group: 'Account', description: 'Opens the Help drawer', keywords: 'contact us question problem docs guide', verb: 'Open', run: 'help', shortcut: '?' },
  { key: 'sign-out', label: 'Sign out', mark: { kind: 'chrome', name: 'signOut' }, group: 'Account', description: 'Ends your session on this device', keywords: 'log out leave exit session', verb: 'Sign out', run: 'signOut' },
]

/** Group render order — Quick action first, matching the reference palette. */
export const QUICK_ACTION_GROUPS: QuickActionGroup[] = ['Quick action', 'Navigate', 'Account']

/** Case-insensitive match across label + keywords. */
export function matchesAction(action: QuickAction, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    action.label.toLowerCase().includes(q) ||
    (action.keywords?.toLowerCase().includes(q) ?? false)
  )
}

/**
 * The label itself begins with the query ("new", "cus") — strong enough to
 * list the Quick actions group above matching records in All. A keyword-only
 * hit ("add") keeps records first.
 */
export function matchesActionStrongly(action: QuickAction, query: string): boolean {
  const q = query.trim().toLowerCase()
  return q.length > 0 && action.label.toLowerCase().startsWith(q)
}
